<script setup lang="ts">
import {computed, ref} from 'vue';
import {verifyApi} from '@/features/auth-verify/api/verify';
import SmartIdCeremony from '@/features/auth-ceremony/SmartIdCeremony.vue';
import type {
    SmartIdCeremonyApi,
    SmartIdCeremonyHandle,
    SmartIdCeremonySuccess
} from '@/features/auth-ceremony/types';
import type {VerifyCompleteResponseDto, VerifyTranslations} from '@/features/auth-verify/types';

defineOptions({name: 'VerifySmartId'});

const props = defineProps<{
    translations: VerifyTranslations;
    purpose: string;
    resourceId: string;
}>();

const emit = defineEmits<{
    (e: 'verified', token: string): void;
    (e: 'cancel'): void;
}>();

const identityNumber = ref('');
const ceremonyRef = ref<SmartIdCeremonyHandle | null>(null);

const t = (key: string, fallback = '') => props.translations[key] ?? fallback ?? key;

const apiConfig: SmartIdCeremonyApi = {
    initiate: (input) => verifyApi.initiateSmartId({identityNumber: input.identityNumber}),
    completeRequest: () => verifyApi.completeSmartIdRequest({
        purpose: props.purpose,
        resourceId: props.resourceId
    })
};

async function handleStart() {
    if (ceremonyRef.value?.isBusy()) return;
    await ceremonyRef.value?.start();
}

function onSuccess(payload: SmartIdCeremonySuccess) {
    const token = (payload.completion as VerifyCompleteResponseDto | undefined)?.token;
    if (token) emit('verified', token);
}

const isBusy = computed(() => ceremonyRef.value?.isBusy() ?? false);
</script>

<template>
    <form class="verify-method" @submit.prevent="handleStart">
        <SmartIdCeremony
            ref="ceremonyRef"
            v-model:identity-number="identityNumber"
            :translations="translations"
            :api-config="apiConfig"
            test-id-prefix="verify-sid"
            @success="onSuccess"
        />

        <div v-if="!isBusy" class="form-controls">
            <button class="js-save-button btn btn-cta" type="submit">
                <span class="js-translatable-span" data-translation-key="btn-start">{{ t('btn-start') }}</span>
            </button>
            <button class="js-cancel-button btn" type="button" @click="emit('cancel')">
                <span class="js-translatable-span" data-translation-key="btn-cancel">{{ t('btn-cancel') }}</span>
            </button>
        </div>
    </form>
</template>

<style scoped>
.verify-method { display: flex; flex-direction: column; gap: 18px; }

.form-controls {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
}
</style>
